import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ClipboardText, PaperPlaneTilt, CheckCircle, FileText, Truck, Package } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Ordered pipeline stages — matches the biotech workflow
const STAGES = [
  { key: 'pending_quotation',  label: 'Pending Quotation',    owner: 'Lab Manager',         icon: ClipboardText },
  { key: 'quotations_added',   label: 'Quotations Added',     owner: 'Operations Manager',  icon: PaperPlaneTilt },
  { key: 'vendor_approved',    label: 'Vendor Approved',      owner: 'Ops (14-day window)', icon: CheckCircle },
  { key: 'po_created',         label: 'PO Uploaded',          owner: 'Ops / Lab Manager',   icon: FileText },
  { key: 'po_released',        label: 'PO Sent to Vendor',    owner: 'Vendor (fulfilling)', icon: Truck },
  { key: 'completed',          label: 'Delivered',            owner: '—',                    icon: Package },
];

const STAGE_LABEL = Object.fromEntries(STAGES.map(s => [s.key, s.label]));
const STAGE_OWNER = Object.fromEntries(STAGES.map(s => [s.key, s.owner]));

const daysAgo = (iso) => {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
};

const AdminWorkflowOverview = () => {
  const { getAuthHeader } = useAuth();
  const [requisitions, setRequisitions] = useState([]);
  const [vendorOrders, setVendorOrders] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [r, vo, po] = await Promise.all([
          axios.get(`${API}/requisitions?limit=1000`, { headers: getAuthHeader() }),
          axios.get(`${API}/vendor-orders`, { headers: getAuthHeader() }),
          axios.get(`${API}/purchase-orders`, { headers: getAuthHeader() }),
        ]);
        setRequisitions(r.data || []);
        setVendorOrders(vo.data || []);
        setPurchaseOrders(po.data || []);
      } catch (e) {
        toast.error('Failed to load workflow overview');
      } finally {
        setLoading(false);
      }
    })();
  }, [getAuthHeader]);

  const stageCounts = useMemo(() => {
    const counts = Object.fromEntries(STAGES.map(s => [s.key, 0]));
    for (const r of requisitions) if (counts[r.status] !== undefined) counts[r.status]++;
    return counts;
  }, [requisitions]);

  const accumulatingVoCount = vendorOrders.filter(v => v.status === 'accumulating').length;
  const readyForPoCount = vendorOrders.filter(v => v.status === 'ready_for_po').length;
  const pendingReleasePoCount = purchaseOrders.filter(p => p.status === 'pending_release').length;

  const filtered = useMemo(() => {
    let out = requisitions;
    if (stageFilter) out = out.filter(r => r.status === stageFilter);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      out = out.filter(r =>
        (r.reagent_name || '').toLowerCase().includes(s) ||
        (r.vendor_name || '').toLowerCase().includes(s) ||
        (r.catalogue_number || '').toLowerCase().includes(s) ||
        (r.employee_name || '').toLowerCase().includes(s) ||
        (r.project_code_name || '').toLowerCase().includes(s)
      );
    }
    // Sort by newest first
    return [...out].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [requisitions, stageFilter, search]);

  if (loading) return <div className="p-8 text-center text-text-tertiary">Loading workflow overview…</div>;

  return (
    <div className="space-y-6" data-testid="admin-workflow-overview">
      {/* Pipeline callouts */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {STAGES.map((s) => {
          const Icon = s.icon;
          const active = stageFilter === s.key;
          const count = stageCounts[s.key] || 0;
          return (
            <button
              type="button"
              key={s.key}
              onClick={() => setStageFilter(active ? null : s.key)}
              className={`text-left rounded-lg border p-3 transition-all ${active ? 'border-accent-brand bg-accent-brand-subtle' : 'border-border-default bg-surface hover:border-accent-brand/60'}`}
              data-testid={`stage-card-${s.key}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon size={18} weight="duotone" className="text-accent-brand" />
                <p className="text-xs uppercase tracking-wider text-text-tertiary font-semibold">{s.label}</p>
              </div>
              <p className="text-3xl font-bold text-text-primary">{count}</p>
              <p className="text-xs text-text-tertiary mt-1">Owner: <span className="font-medium">{s.owner}</span></p>
            </button>
          );
        })}
      </div>

      {/* Aggregate vendor-order / PO stats */}
      <Card className="p-4 bg-accent-brand-subtle/30 border-accent-brand/20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-text-tertiary">Vendor Orders accumulating (14-day window)</p>
            <p className="text-2xl font-bold text-text-primary">{accumulatingVoCount}</p>
          </div>
          <div>
            <p className="text-text-tertiary">Vendor Orders ready for PO (Finance)</p>
            <p className="text-2xl font-bold text-amber-700">{readyForPoCount}</p>
          </div>
          <div>
            <p className="text-text-tertiary">POs awaiting release to vendor</p>
            <p className="text-2xl font-bold text-amber-700">{pendingReleasePoCount}</p>
          </div>
        </div>
      </Card>

      {/* Filter row */}
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
        <div className="flex items-center gap-2">
          {stageFilter && (
            <span className="text-sm">
              Filtered by <span className="font-semibold text-text-primary">{STAGE_LABEL[stageFilter]}</span>
              <button
                type="button"
                onClick={() => setStageFilter(null)}
                className="ml-2 text-xs text-accent-brand hover:underline"
                data-testid="clear-stage-filter"
              >
                Clear
              </button>
            </span>
          )}
        </div>
        <Input
          placeholder="Search reagent, vendor, cat #, employee, or project…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="md:w-96"
          data-testid="workflow-search-input"
        />
      </div>

      {/* Requisitions table */}
      <div className="border border-border-default rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface border-b border-border-default">
            <tr>
              <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-text-secondary">Reagent</th>
              <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-text-secondary">Vendor</th>
              <th className="text-right p-3 text-xs font-bold uppercase tracking-wider text-text-secondary">Qty</th>
              <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-text-secondary">Project</th>
              <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-text-secondary">Employee</th>
              <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-text-secondary">Current Stage</th>
              <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-text-secondary">Next Action</th>
              <th className="text-right p-3 text-xs font-bold uppercase tracking-wider text-text-secondary">Age</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan="8" className="p-8 text-center text-text-tertiary">
                  {stageFilter ? 'No requisitions in this stage.' : 'No requisitions match your search.'}
                </td>
              </tr>
            )}
            {filtered.map((req) => (
              <tr
                key={req.id}
                className="border-b border-border-default hover:bg-surface transition-colors"
                data-testid={`workflow-req-${req.id}`}
              >
                <td className="p-3">
                  <div className="font-medium text-text-primary">{req.reagent_name}</div>
                  <div className="text-xs text-text-tertiary font-mono">{req.catalogue_number}</div>
                </td>
                <td className="p-3 text-text-secondary">{req.approved_vendor || req.vendor_name}</td>
                <td className="p-3 text-right font-medium">{req.quantity ?? 1}</td>
                <td className="p-3 text-text-secondary">{req.project_code_name}</td>
                <td className="p-3 text-text-secondary">{req.employee_name}</td>
                <td className="p-3">
                  <span className="status-badge status-info">{STAGE_LABEL[req.status] || req.status}</span>
                </td>
                <td className="p-3 text-text-secondary">{STAGE_OWNER[req.status] || '—'}</td>
                <td className="p-3 text-right text-text-tertiary text-xs">
                  {daysAgo(req.created_at) === 0 ? 'today' : `${daysAgo(req.created_at)}d ago`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-text-tertiary">
        Total requisitions: <span className="font-semibold text-text-primary">{requisitions.length}</span> · Showing{' '}
        <span className="font-semibold text-text-primary">{filtered.length}</span>
      </p>
    </div>
  );
};

export default AdminWorkflowOverview;
